import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Star } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { SdTestAppBenchClient } from "@/lib/sdtestapp/sdTestAppBenchClient";
import type { SdTestDevice } from "@/lib/sdtestapp/sdTestAppHttpClient";
import {
  readPolledDeviceFirmware,
  readPolledDeviceModelNumber,
  readPolledDeviceMcuUuid,
} from "../configurationDeviceFields";
import { ConfigurationDevicePolledPanel } from "../ConfigurationDevicePolledPanel";
import { BenchFineTuneCalibrationModal } from "./BenchFineTuneCalibrationModal";

export function BenchMainTab({
  port,
  machineId,
  devices,
  canActuate,
  observeOnly = false,
  selectedConnectorId,
  onSelectConnector,
  referenceConnectorId,
  onReferenceChange,
}: {
  port: string;
  machineId?: string;
  devices: SdTestDevice[];
  canActuate: boolean;
  observeOnly?: boolean;
  selectedConnectorId: string | null;
  onSelectConnector: (connectorId: string) => void;
  referenceConnectorId: string | null;
  onReferenceChange: () => void;
}): JSX.Element {
  const client = useMemo(() => new SdTestAppBenchClient(port), [port]);
  const activeDevice = devices.find((device) => String(device.connectorId ?? "") === selectedConnectorId) ?? null;
  const [consoleMode, setConsoleMode] = useState<"ascii" | "m2m">("ascii");
  const [consoleText, setConsoleText] = useState("");
  const [powerPercent, setPowerPercent] = useState("10");
  const [powerStatus, setPowerStatus] = useState<string>("");
  const [modelNumber, setModelNumber] = useState("");
  const [configureMessage, setConfigureMessage] = useState<string | null>(null);
  const [consoleLogLines, setConsoleLogLines] = useState<string[]>([]);
  const [calibrationPoints, setCalibrationPoints] = useState<
    import("@/lib/sdtestapp/sdTestAppBenchClient").BenchAppCalibrationPoint[]
  >([]);
  const [calPointDrafts, setCalPointDrafts] = useState<Record<number, { amps: string; percent: string }>>({});
  const [fineTuneOpen, setFineTuneOpen] = useState(false);
  const [fineTuneCanOpen, setFineTuneCanOpen] = useState(false);
  const [fineTuneGateReason, setFineTuneGateReason] = useState<string | null>(null);
  const [manualVolumePercent, setManualVolumePercent] = useState("");
  const [powerSequence, setPowerSequence] = useState("");
  const [calCsvMessage, setCalCsvMessage] = useState<string | null>(null);
  const csvInputRef = useRef<HTMLInputElement | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshPowerStatus = useCallback(async () => {
    if (!canActuate) return;
    try {
      const status = await client.fetchPowerStatus();
      setPowerStatus(
        [
          status.appCalibrationStatusText,
          status.calibrationStatusText,
          status.powerRunning ? "Power running" : "Power idle",
        ]
          .filter(Boolean)
          .join(" · "),
      );
    } catch {
      /* ignore polling errors */
    }
  }, [canActuate, client]);

  useEffect(() => {
    void refreshPowerStatus();
    if (!canActuate) return undefined;
    const intervalId = setInterval(() => void refreshPowerStatus(), 2500);
    return () => clearInterval(intervalId);
  }, [canActuate, refreshPowerStatus]);

  useEffect(() => {
    if (activeDevice) {
      setModelNumber(readPolledDeviceModelNumber(activeDevice));
    }
  }, [activeDevice]);

  useEffect(() => {
    if (!selectedConnectorId) {
      setConsoleLogLines([]);
      return undefined;
    }
    const loadLog = async () => {
      try {
        const lines =
          observeOnly && machineId
            ? await SdTestAppBenchClient.relayConsoleLog(machineId, selectedConnectorId)
            : await client.fetchConsoleLog(selectedConnectorId);
        setConsoleLogLines(lines);
      } catch {
        /* ignore polling errors */
      }
    };
    void loadLog();
    if (!canActuate && !(observeOnly && machineId)) return undefined;
    const intervalId = setInterval(() => void loadLog(), 1500);
    return () => clearInterval(intervalId);
  }, [canActuate, client, machineId, observeOnly, selectedConnectorId]);

  useEffect(() => {
    if (!canActuate) return undefined;
    void client
      .fetchAppCalibrationPoints()
      .then((rows) => {
        setCalibrationPoints(rows);
        const drafts: Record<number, { amps: string; percent: string }> = {};
        for (const row of rows) {
          drafts[row.pointIndex] = {
            amps: row.measuredAmps == null ? "" : String(row.measuredAmps),
            percent: row.outputPercent == null ? "" : String(row.outputPercent),
          };
        }
        setCalPointDrafts(drafts);
      })
      .catch(() => undefined);
    return undefined;
  }, [canActuate, client]);

  useEffect(() => {
    if (!canActuate) return undefined;
    void client
      .fetchFineTuneSnapshot()
      .then((snapshot) => {
        setFineTuneCanOpen(snapshot.canOpen);
        setFineTuneGateReason(snapshot.gateReason ?? null);
      })
      .catch(() => undefined);
    return undefined;
  }, [canActuate, client, pending]);

  const runAction = async (action: () => Promise<void>) => {
    setError(null);
    setPending(true);
    try {
      await action();
      await refreshPowerStatus();
      onReferenceChange();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : String(actionError));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <div className="space-y-3 rounded border p-3">
        <h3 className="font-semibold">Connected devices</h3>
        <ul className="space-y-2">
          {devices.map((device) => {
            const connectorId = String(device.connectorId ?? "");
            const isReference = referenceConnectorId === connectorId;
            return (
              <li key={connectorId} className="rounded border px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    className="text-left font-mono text-xs hover:underline"
                    onClick={() => onSelectConnector(connectorId)}
                  >
                    {connectorId}
                  </button>
                  <Button
                    type="button"
                    size="sm"
                    variant={isReference ? "primary" : "secondary"}
                    disabled={!canActuate || pending}
                    onClick={() => void runAction(async () => {
                      await client.setReferenceDevice(connectorId);
                    })}
                  >
                    <Star className="mr-1 h-3 w-3" />
                    {isReference ? "Reference" : "Make reference"}
                  </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {readPolledDeviceModelNumber(device)} · {readPolledDeviceFirmware(device)}
                </p>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="space-y-4 rounded border p-3">
        <h3 className="font-semibold">Unit data</h3>
        {selectedConnectorId ? (
          <ConfigurationDevicePolledPanel
            activeDevice={activeDevice}
            showImei={false}
            connectorId={selectedConnectorId}
            refreshPending={pending}
            refreshNotice={null}
            onRefreshConnector={() => void runAction(async () => {
              if (!selectedConnectorId) return;
              await client.queryBattery(selectedConnectorId);
            })}
            refreshEnabled={canActuate}
            refreshDisabled={!canActuate}
          />
        ) : (
          <p className="text-sm text-muted-foreground">Select a device to view unit data.</p>
        )}
        {activeDevice ? (
          <p className="text-xs text-muted-foreground">
            MCU {readPolledDeviceMcuUuid(activeDevice) || "—"}
          </p>
        ) : null}
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[140px]">
            <Label htmlFor="bench-configure-sku">Configure SKU</Label>
            <Input
              id="bench-configure-sku"
              className="mt-1 font-mono text-sm uppercase"
              value={modelNumber}
              disabled={!canActuate}
              onChange={(event) => setModelNumber(event.target.value.toUpperCase())}
            />
          </div>
          <Button
            type="button"
            size="sm"
            disabled={!canActuate || !selectedConnectorId || pending || modelNumber.trim() === ""}
            onClick={() => void runAction(async () => {
              if (!selectedConnectorId) return;
              const result = await client.configureDeviceSku(selectedConnectorId, modelNumber.trim());
              if (!result.success) {
                throw new Error(result.error ?? "SKU configuration failed");
              }
              setConfigureMessage(`Configured ${modelNumber.trim()} on ${selectedConnectorId}.`);
            })}
          >
            Apply SKU
          </Button>
        </div>
        {configureMessage ? <p className="text-xs text-muted-foreground">{configureMessage}</p> : null}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!canActuate || !selectedConnectorId || pending}
            onClick={() => void runAction(async () => {
              if (!selectedConnectorId) return;
              await client.queryBattery(selectedConnectorId);
            })}
          >
            Requery battery
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!canActuate || !selectedConnectorId || pending}
            onClick={() => void runAction(async () => {
              if (!selectedConnectorId) return;
              await client.setFastMode(selectedConnectorId, true);
            })}
          >
            Fast mode on
          </Button>
        </div>
      </div>

      <div className="space-y-3 rounded border p-3 xl:col-span-2">
        <h3 className="font-semibold">Device console log</h3>
        {selectedConnectorId ? (
          <pre className="max-h-48 overflow-auto rounded border bg-muted/30 p-2 font-mono text-xs">
            {consoleLogLines.length > 0 ? consoleLogLines.join("\n") : "No console output yet."}
          </pre>
        ) : (
          <p className="text-sm text-muted-foreground">Select a device to view its console log.</p>
        )}
      </div>

      <div className="space-y-3 rounded border p-3 xl:col-span-2">
        <h3 className="font-semibold">Command console</h3>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant={consoleMode === "ascii" ? "primary" : "secondary"} onClick={() => setConsoleMode("ascii")}>
            ASCII
          </Button>
          <Button type="button" size="sm" variant={consoleMode === "m2m" ? "primary" : "secondary"} onClick={() => setConsoleMode("m2m")}>
            M2M
          </Button>
        </div>
        <Input value={consoleText} onChange={(event) => setConsoleText(event.target.value)} placeholder={consoleMode === "m2m" ? "Hex bytes" : "ASCII command"} disabled={!canActuate} />
        <Button
          type="button"
          disabled={!canActuate || !selectedConnectorId || pending || consoleText.trim() === ""}
          onClick={() => void runAction(async () => {
            if (!selectedConnectorId) return;
            await client.sendConsole(
              selectedConnectorId,
              consoleMode === "m2m"
                ? { mode: "m2m", hex: consoleText.trim() }
                : { mode: "ascii", text: consoleText.trim() },
            );
            setConsoleText("");
          })}
        >
          Send
        </Button>
      </div>

      <div className="space-y-3 rounded border p-3 xl:col-span-2">
        <h3 className="font-semibold">Application calibration points</h3>
        <p className="text-sm text-muted-foreground">
          Edit measured amps and output percent for each calibration point (amps first, then percent — same as NiceGUI Main tab).
        </p>
        {canActuate ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={pending}
              onClick={() =>
                void runAction(async () => {
                  const payload = await client.exportAppCalibrationCsv();
                  const blob = new Blob([payload.csvText], { type: "text/csv;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const anchor = document.createElement("a");
                  anchor.href = url;
                  anchor.download = payload.filename || "coil_calibration_points.csv";
                  anchor.click();
                  URL.revokeObjectURL(url);
                  setCalCsvMessage("Exported calibration CSV.");
                })
              }
            >
              Export CSV
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={pending}
              onClick={() => csvInputRef.current?.click()}
            >
              Import CSV
            </Button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                void runAction(async () => {
                  const csvText = await file.text();
                  const result = await client.importAppCalibrationCsv(csvText);
                  if (!result.success) {
                    throw new Error(result.error ?? "CSV import failed");
                  }
                  const rows = await client.fetchAppCalibrationPoints();
                  setCalibrationPoints(rows);
                  const drafts: Record<number, { amps: string; percent: string }> = {};
                  for (const row of rows) {
                    drafts[row.pointIndex] = {
                      amps: row.measuredAmps == null ? "" : String(row.measuredAmps),
                      percent: row.outputPercent == null ? "" : String(row.outputPercent),
                    };
                  }
                  setCalPointDrafts(drafts);
                  setCalCsvMessage(`Imported ${rows.length} calibration points from CSV.`);
                }).finally(() => {
                  event.target.value = "";
                });
              }}
            />
          </div>
        ) : null}
        {calCsvMessage ? <p className="text-xs text-muted-foreground">{calCsvMessage}</p> : null}
        {canActuate ? (
          <div className="max-h-56 overflow-auto rounded border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/80">
                <tr className="border-b text-left">
                  <th className="px-2 py-1">Point</th>
                  <th className="px-2 py-1">Amps</th>
                  <th className="px-2 py-1">Percent</th>
                  <th className="px-2 py-1" />
                </tr>
              </thead>
              <tbody>
                {calibrationPoints.map((point) => (
                  <tr key={point.pointIndex} className="border-b last:border-b-0">
                    <td className="px-2 py-1 font-medium">
                      {point.pointIndex === 1
                        ? "1 (0A)"
                        : point.targetAmps != null
                          ? `${point.pointIndex} (~${Math.round(point.targetAmps)}A)`
                          : point.pointIndex}
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        className="h-7 font-mono text-xs"
                        value={calPointDrafts[point.pointIndex]?.amps ?? ""}
                        onChange={(event) =>
                          setCalPointDrafts((current) => ({
                            ...current,
                            [point.pointIndex]: {
                              amps: event.target.value,
                              percent: current[point.pointIndex]?.percent ?? "",
                            },
                          }))
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Input
                        className="h-7 font-mono text-xs"
                        value={calPointDrafts[point.pointIndex]?.percent ?? ""}
                        onChange={(event) =>
                          setCalPointDrafts((current) => ({
                            ...current,
                            [point.pointIndex]: {
                              amps: current[point.pointIndex]?.amps ?? "",
                              percent: event.target.value,
                            },
                          }))
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={pending}
                        onClick={() =>
                          void runAction(async () => {
                            const draft = calPointDrafts[point.pointIndex];
                            await client.saveAppCalibrationPoint({
                              pointIndex: point.pointIndex,
                              measuredAmps: Number.parseFloat(draft?.amps ?? "0"),
                              outputPercent: Number.parseFloat(draft?.percent ?? "0"),
                            });
                          })
                        }
                      >
                        Save
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Local actuation required to edit calibration points.</p>
        )}
      </div>

      <div className="space-y-3 rounded border p-3 xl:col-span-2">
        <h3 className="font-semibold">Manual volume</h3>
        <p className="text-sm text-muted-foreground">
          Raw output gain percent (0–95). Does not require calibration or a reference device.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label htmlFor="bench-manual-volume-percent">Output gain %</Label>
            <Input
              id="bench-manual-volume-percent"
              className="mt-1 w-28 font-mono"
              value={manualVolumePercent}
              disabled={!canActuate}
              onChange={(event) => setManualVolumePercent(event.target.value)}
            />
          </div>
          <Button
            type="button"
            disabled={!canActuate || pending || manualVolumePercent.trim() === ""}
            onClick={() =>
              void runAction(async () => {
                await client.startManualVolume(Number.parseFloat(manualVolumePercent));
              })
            }
          >
            Play
          </Button>
          <Button type="button" variant="secondary" disabled={!canActuate || pending} onClick={() => void runAction(() => client.stopPower())}>
            Stop
          </Button>
        </div>
      </div>

      <div className="space-y-3 rounded border p-3 xl:col-span-2">
        <h3 className="font-semibold">Power sequence</h3>
        <p className="text-sm text-muted-foreground">
          Amps-based sequence string (semicolon-separated steps). Requires app calibration and a reference device.
        </p>
        <Input
          value={powerSequence}
          disabled={!canActuate}
          placeholder="e.g. 5A 2s; 10A 3s"
          onChange={(event) => setPowerSequence(event.target.value)}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={!canActuate || pending || powerSequence.trim() === ""}
            onClick={() => void runAction(async () => {
              await client.startPowerSequence(powerSequence.trim());
            })}
          >
            Run sequence
          </Button>
          <Button type="button" variant="secondary" disabled={!canActuate || pending} onClick={() => void runAction(() => client.stopPower())}>
            Stop sequence
          </Button>
        </div>
      </div>

      <div className="space-y-3 rounded border p-3 xl:col-span-2">
        <h3 className="font-semibold">Application calibration and power</h3>
        <p className="text-sm text-muted-foreground">{powerStatus || "Power status unavailable"}</p>
        <div className="flex flex-wrap items-end gap-2">
          <Button type="button" disabled={!canActuate || pending} onClick={() => void runAction(() => client.startCalibration())}>
            Start coil calibration
          </Button>
          <Button type="button" variant="secondary" disabled={!canActuate || pending} onClick={() => void runAction(() => client.cancelCalibration())}>
            Cancel calibration
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!canActuate || pending || !fineTuneCanOpen}
            title={fineTuneGateReason ?? undefined}
            onClick={() => setFineTuneOpen(true)}
          >
            Fine-tune calibration
          </Button>
          <div>
            <Label htmlFor="bench-power-percent">Output percent</Label>
            <Input id="bench-power-percent" className="w-24" value={powerPercent} onChange={(event) => setPowerPercent(event.target.value)} />
          </div>
          <Button
            type="button"
            disabled={!canActuate || pending}
            onClick={() => void runAction(async () => {
              await client.startPower(Number(powerPercent));
            })}
          >
            Start power
          </Button>
          <Button type="button" variant="secondary" disabled={!canActuate || pending} onClick={() => void runAction(() => client.stopPower())}>
            Stop power
          </Button>
        </div>
      </div>

      {pending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground xl:col-span-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Working…
        </p>
      ) : null}
      {error ? <p className="text-sm text-destructive xl:col-span-2">{error}</p> : null}
      {!canActuate && !observeOnly ? (
        <p className="text-sm text-muted-foreground xl:col-span-2">Local SDTestApp actuation required for Main tab controls.</p>
      ) : observeOnly ? (
        <p className="text-sm text-muted-foreground xl:col-span-2">Remote observe: device list and console log only. Actuation requires the bench PC.</p>
      ) : null}

      <BenchFineTuneCalibrationModal
        open={fineTuneOpen}
        port={port}
        onClose={() => setFineTuneOpen(false)}
        onComplete={() => {
          void client.fetchAppCalibrationPoints().then(setCalibrationPoints).catch(() => undefined);
        }}
      />
    </div>
  );
}
